import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import AdBanner from './AdBanner';
import MandatoryAd from './MandatoryAd';

function Discovery({ onChatOpen }) {
  const [profiles, setProfiles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [currentUserPreferences, setCurrentUserPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [profilePhotos, setProfilePhotos] = useState({});
  const [showMatchPopup, setShowMatchPopup] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [isUberMatch, setIsUberMatch] = useState(false);
  // Subscription and likes tracking
  const [subscription, setSubscription] = useState(null);
  const [dailyLikes, setDailyLikes] = useState({ likes_used: 0 });
  const [likesLoading, setLikesLoading] = useState(false);
  
  // Super likes tracking
  const [superLikesReceived, setSuperLikesReceived] = useState([]);
  
  // Ad tracking
  const [adCounter, setAdCounter] = useState(0);
  const [showAd, setShowAd] = useState(false);
  const [availableAds, setAvailableAds] = useState([]);
  const [currentAdData, setCurrentAdData] = useState(null);

  // Mandatory ads tracking
  const [showMandatoryAd, setShowMandatoryAd] = useState(false);
  const [mandatoryAdData, setMandatoryAdData] = useState(null);
  const [mandatoryAdType, setMandatoryAdType] = useState(null);
  const [dailyMandatoryAdsShown, setDailyMandatoryAdsShown] = useState(0);
  const [sessionMandatoryAdShown, setSessionMandatoryAdShown] = useState(() => {
    return sessionStorage.getItem('mandatoryAdShown') === 'true';
  });



  useEffect(() => {
    // Prioritize critical data for Discovery page
    const initializeDiscovery = async () => {
      // Load user and profiles first (blocking)
      await getCurrentUser();
      await fetchProfiles();
      
      // Load other data in parallel (non-blocking)
      Promise.all([
        fetchMatches(),
        fetchSubscription(),
        fetchDailyLikes(),
        fetchSuperLikesReceived(),
        fetchAvailableAds()
      ]).catch(error => {
        console.error('Non-critical data loading error:', error);
      });
    };
    
    initializeDiscovery();
  }, []);

  useEffect(() => {
    // Reset photo index when profile changes
    setCurrentPhotoIndex(0);
    // Preload photos for current and next 2 profiles for better UX
    if (profiles.length > 0) {
      const indicesToPreload = [
        currentProfileIndex,
        currentProfileIndex + 1,
        currentProfileIndex + 2
      ].filter(index => index < profiles.length && profiles[index]);
      
      indicesToPreload.forEach(index => {
        const profile = profiles[index];
        if (profile && !profilePhotos[profile.id]) {
          fetchProfilePhotos(profile.id);
        }
      });
    }
  }, [currentProfileIndex, profiles, profilePhotos]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);

  // Check for mandatory ad AFTER currentUser is set
    if (user && !sessionMandatoryAdShown) {
    setTimeout(() => {
      checkMandatoryAd(user, 'discovery'); 
    }, 500);
  }
};
  // Fetch user's subscription status
  const fetchSubscription = async () => {
    
    try {
    // Temporary: Set fake subscription for testing
    setSubscription({ is_premium: false });
  } catch (error) {
    console.error('Error fetching subscription:', error);
  }
};

  // Fetch super likes received by current user
  const fetchSuperLikesReceived = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('super_likes')
        .select('from_user_id')
        .eq('to_user_id', user.id);

      if (error) throw error;
      setSuperLikesReceived(data?.map(sl => sl.from_user_id) || []);
    } catch (error) {
      console.error('Error fetching super likes:', error);
    }
  };

  // Fetch available banner ads
  const fetchAvailableAds = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;
      setAvailableAds(data || []);
    } catch (error) {
      console.error('Error fetching available ads:', error);
    }
  };

  // Show a random ad if ads are available
  const showRandomAd = () => {
    if (availableAds.length > 0) {
      const randomAd = availableAds[Math.floor(Math.random() * availableAds.length)];
      setCurrentAdData(randomAd);
      setShowAd(true);
    }
    // If no ads available, don't show anything
  };

  // Check if current profile super liked me
  const didSuperLikeMe = (userId) => {
    return superLikesReceived.includes(userId);
  };

  // Fetch today's likes usage
  const fetchDailyLikes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('daily_likes')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .single();

      if (data) {
        setDailyLikes(data);
      } else {
        // Create today's record if it doesn't exist
        const { data: newRecord, error: insertError } = await supabase
          .from('daily_likes')
          .insert({
            user_id: user.id,
            likes_used: 0,
            date: today
          })
          .select()
          .single();

        if (newRecord) {
          setDailyLikes(newRecord);
        }
      }
    } catch (error) {
      console.error('Error fetching daily likes:', error);
    }
  };

  // Update daily likes count
  const incrementDailyLikes = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const today = new Date().toISOString().split('T')[0];
    
    // Use upsert to handle both insert and update
    const { data, error } = await supabase
      .from('daily_likes')
      .upsert({
        user_id: user.id,
        date: today,
        likes_used: (dailyLikes?.likes_used || 0) + 1
      }, {
        onConflict: 'user_id,date'
      })
      .select()
      .single();

    if (data && !error) {
      setDailyLikes(data);
    }
  } catch (error) {
    console.error('Error updating daily likes:', error);
  }
};

  // Check if user can like (premium or within daily limit)
  const canLike = () => {
    if (subscription?.is_premium) return true;
    return dailyLikes.likes_used < 10; // Free users get 10 likes per day
  };

  const fetchMatches = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get mutual matches (users who liked each other)
      const { data: myLikes } = await supabase
        .from('matches')
        .select('liked_user_id')
        .eq('user_id', user.id);

      const { data: likesOnMe } = await supabase
        .from('matches')
        .select('user_id')
        .eq('liked_user_id', user.id);

      // Find mutual matches
      const myLikedIds = myLikes?.map(like => like.liked_user_id) || [];
      const whoLikedMeIds = likesOnMe?.map(like => like.user_id) || [];
      
      const mutualMatches = myLikedIds.filter(id => whoLikedMeIds.includes(id));
      setMatches(mutualMatches);

    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  // Check if two users are gender compatible based on their preferences
  const checkGenderCompatibility = (currentUser, targetProfile) => {
    // If either user hasn't set their gender preferences, show them (for now)
    if (!currentUser?.gender || !currentUser?.looking_for || !targetProfile?.gender || !targetProfile?.looking_for) {
      return true;
    }

    // Check if current user's preference matches target's gender
    const currentUserCompatible = 
      currentUser.looking_for === 'everyone' || 
      (currentUser.looking_for === 'men' && targetProfile.gender === 'male') ||
      (currentUser.looking_for === 'women' && targetProfile.gender === 'female');

    // Check if target user's preference matches current user's gender  
    const targetUserCompatible = 
      targetProfile.looking_for === 'everyone' ||
      (targetProfile.looking_for === 'men' && currentUser.gender === 'male') ||
      (targetProfile.looking_for === 'women' && currentUser.gender === 'female');

    // Both users must be compatible with each other
    return currentUserCompatible && targetUserCompatible;
  };

  // Check if age is within preferred range for both users
  const checkAgeCompatibility = (currentUser, targetProfile) => {
    if (!targetProfile?.age) {
      return false; // Don't show profiles without age
    }

    const targetAge = parseInt(targetProfile.age);
    const currentUserAge = parseInt(currentUser.age);

    // Use default age range if not set (18-50)
    const currentUserMinAge = currentUser?.age_range_min || 18;
    const currentUserMaxAge = currentUser?.age_range_max || 50;
    const targetMinAge = targetProfile?.age_range_min || 18;
    const targetMaxAge = targetProfile?.age_range_max || 50;

    // Check if target's age is within current user's preferred range
    const ageInRange = targetAge >= currentUserMinAge && targetAge <= currentUserMaxAge;

    // Check if current user's age is within target's preferred range
    const currentUserInTargetRange = currentUserAge >= targetMinAge && currentUserAge <= targetMaxAge;

    return ageInRange && currentUserInTargetRange;
  };

  // Add state for infinite scroll and batching
  const [profileBatch, setProfileBatch] = useState(0);
  const [hasMoreProfiles, setHasMoreProfiles] = useState(true);
  const [loadingMoreProfiles, setLoadingMoreProfiles] = useState(false);

  const fetchProfiles = async (batchNumber = 0, append = false) => {
  try {
    if (!append) setLoading(true);
    setLoadingMoreProfiles(true);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    // Get current user profile first
    const { data: currentUserProfile } = await supabase
      .from('profiles')
      .select('name, age, gender, looking_for, age_range_min, age_range_max, preferences')
      .eq('id', user.id)
      .single();
    
    if (!append) {
      // Store current user profile in state
      setCurrentUserProfile(currentUserProfile);
      
      // Early validation checks
      if (!currentUserProfile?.name || !currentUserProfile?.age) {
        alert('Please complete your profile with your name and age before discovering others.');
        window.location.hash = '#profile';
        return;
      }
      
      if (parseInt(currentUserProfile.age) < 18) {
        alert('You must be 18 or older to use Flirteen.');
        window.location.hash = '#profile';
        return;
      }
    }
    
    // Get recent user actions (last 1000 only for performance)
    const { data: userActions } = await supabase
      .from('user_actions')
      .select('target_user_id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1000);
    
    const actionedUserIds = userActions?.map(action => action.target_user_id) || [];
    
    // Build highly optimized query with smart ordering
    let query = supabase
      .from('profiles')
      .select('id, name, age, gender, looking_for, age_range_min, age_range_max, preferences, photo_url, location, bio')
      .neq('id', user.id)
      .not('name', 'is', null)
      .not('photo_url', 'is', null)
      .not('age', 'is', null)
      .gte('age', 18);
    
    // Exclude recent actions to avoid re-showing same people
    if (actionedUserIds.length > 0 && actionedUserIds.length < 50) {
      query = query.not('id', 'in', `(${actionedUserIds.join(',')})`);
    }
    
    // Apply user's gender preference
    const lookingFor = currentUserProfile.looking_for;
    if (lookingFor && lookingFor !== 'everyone') {
      const targetGender = lookingFor === 'men' ? 'male' : 'female';
      query = query.eq('gender', targetGender);
    }
    
    // Apply user's age range preference
    if (currentUserProfile.age_range_min && currentUserProfile.age_range_max) {
      query = query
        .gte('age', currentUserProfile.age_range_min)
        .lte('age', currentUserProfile.age_range_max);
    }
    
    // Use pagination with random sampling for variety
    const batchSize = 20;
    const offset = batchNumber * batchSize;
    
    // Add randomization to prevent showing same profiles in same order
    query = query
      .order('created_at', { ascending: false }) // Recently joined users first
      .range(offset, offset + batchSize - 1);
    
    const { data: profiles, error } = await query;
    
    if (error) {
      console.error('Error fetching profiles:', error);
      return;
    }
    
    if (!profiles || profiles.length === 0) {
      setHasMoreProfiles(false);
      if (!append) setProfiles([]);
      return;
    }
    
    // Fast client-side filtering for mutual compatibility
    const currentGender = currentUserProfile.gender;
    const currentAge = parseInt(currentUserProfile.age);
    
    let filteredProfiles = profiles.filter(profile => {
      // Skip if in recent actions (when too many to exclude via query)
      if (actionedUserIds.length >= 50 && actionedUserIds.includes(profile.id)) {
        return false;
      }
      
      // Check mutual gender compatibility
      if (profile.looking_for && profile.looking_for !== 'everyone') {
        const acceptsCurrentUser = 
          (profile.looking_for === 'men' && currentGender === 'male') ||
          (profile.looking_for === 'women' && currentGender === 'female');
        if (!acceptsCurrentUser) return false;
      }
      
      // Check if current user fits in their age range
      if (profile.age_range_min && profile.age_range_max) {
        if (currentAge < profile.age_range_min || currentAge > profile.age_range_max) {
          return false;
        }
      }
      
      return true;
    });
    
    // Only calculate compatibility for first batch to avoid performance hit
    if (batchNumber === 0 && currentUserProfile?.preferences) {
      filteredProfiles = filteredProfiles.map(profile => ({
        ...profile,
        compatibilityScore: calculateCompatibility(currentUserProfile.preferences, profile.preferences)
      }));
      
      // Sort by compatibility score (highest first)
      filteredProfiles.sort((a, b) => (b.compatibilityScore || 0) - (a.compatibilityScore || 0));
    }
    
    console.log(`🔍 Batch ${batchNumber} - Filtered profiles:`, filteredProfiles.length);
    
    if (append) {
      setProfiles(prev => [...prev, ...filteredProfiles]);
    } else {
      setProfiles(filteredProfiles);
    }
    
    // Check if we have more profiles
    setHasMoreProfiles(profiles.length === batchSize);
    
  } catch (error) {
    console.error('Error:', error);
  } finally {
    setLoading(false);
    setLoadingMoreProfiles(false);
  }
};

  // Load more profiles when user is near the end
  const loadMoreProfiles = async () => {
    if (!hasMoreProfiles || loadingMoreProfiles) return;
    
    const newBatch = profileBatch + 1;
    setProfileBatch(newBatch);
    await fetchProfiles(newBatch, true);
  };

  // Check if we need to load more profiles (when user has 5 or fewer left)
  useEffect(() => {
    const profilesRemaining = profiles.length - currentProfileIndex;
    if (profilesRemaining <= 5 && hasMoreProfiles && !loadingMoreProfiles) {
      loadMoreProfiles();
    }
  }, [currentProfileIndex, profiles.length, hasMoreProfiles, loadingMoreProfiles]);

  // Calculate compatibility score between two users' preferences
  const calculateCompatibility = (userPrefs, otherUserPrefs) => {
    if (!userPrefs || !otherUserPrefs) return 0;
    
    let totalScore = 0;
    let totalCategories = 0;
    
    const categories = ['relationship', 'lifestyle', 'interests', 'social', 'drinking', 'smoking', 'exercise'];
    
    categories.forEach(category => {
      const userCategoryPrefs = userPrefs[category] || [];
      const otherUserCategoryPrefs = otherUserPrefs[category] || [];
      
      if (userCategoryPrefs.length > 0 && otherUserCategoryPrefs.length > 0) {
        // Calculate overlap percentage for this category
        const commonPrefs = userCategoryPrefs.filter(pref => 
          otherUserCategoryPrefs.includes(pref)
        );
        
        const categoryScore = commonPrefs.length / Math.max(userCategoryPrefs.length, otherUserCategoryPrefs.length);
        
        // Weight different categories
        const weights = {
          relationship: 3,    // Most important
          interests: 2,       // Very important
          lifestyle: 2,       // Very important
          social: 1.5,        // Important
          drinking: 1,        // Somewhat important
          smoking: 1,         // Somewhat important
          exercise: 1         // Somewhat important
        };
        
        totalScore += categoryScore * (weights[category] || 1);
        totalCategories += (weights[category] || 1);
      }
    });
    
    return totalCategories > 0 ? (totalScore / totalCategories) * 100 : 0;
  };

  // Get common interests between current user and another user
  const getCommonInterests = (otherProfile) => {
    if (!currentUserPreferences || !otherProfile.preferences) return [];
    
    const commonInterests = [];
    const categories = ['relationship', 'lifestyle', 'interests', 'social', 'drinking', 'smoking', 'exercise'];
    
    categories.forEach(category => {
      const userPrefs = currentUserPreferences[category] || [];
      const otherPrefs = otherProfile.preferences[category] || [];
      
      const common = userPrefs.filter(pref => otherPrefs.includes(pref));
      commonInterests.push(...common);
    });
    
    return [...new Set(commonInterests)]; // Remove duplicates
  };

  const fetchProfilePhotos = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profile_photos')
        .select('*')
        .eq('user_id', userId)
        .order('upload_order', { ascending: true });

      if (data) {
        setProfilePhotos(prev => ({
          ...prev,
          [userId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching profile photos:', error);
    }
  };

  const getCurrentProfilePhotos = () => {
    const currentProfile = profiles[currentProfileIndex];
    if (!currentProfile) return [];
    
    const photos = profilePhotos[currentProfile.id] || [];
    // If no photos in gallery, use main profile photo as fallback
    if (photos.length === 0 && currentProfile.photo_url) {
      return [{ photo_url: currentProfile.photo_url, id: 'main' }];
    }
    return photos;
  };

  const nextPhoto = () => {
    const photos = getCurrentProfilePhotos();
    setCurrentPhotoIndex((prev) => 
      prev < photos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    const photos = getCurrentProfilePhotos();
    setCurrentPhotoIndex((prev) => 
      prev > 0 ? prev - 1 : photos.length - 1
    );
  };

  const handleSuperLike = async (likedUserId) => {
    if (!subscription?.is_premium) {
      alert('Super Likes are a Premium feature! Upgrade to send Super Likes that stand out.');
      return;
    }

    setLikesLoading(true);
    
    try {
      // Check if already super liked this user
      const { data: existingSuperLike } = await supabase
        .from('super_likes')
        .select('*')
        .eq('from_user_id', currentUser.id)
        .eq('to_user_id', likedUserId)
        .single();

      if (existingSuperLike) {
        alert('You\'ve already Super Liked this person!');
        setLikesLoading(false);
        return;
      }

      // Record the super like
      const { error: superLikeError } = await supabase
        .from('super_likes')
        .insert({
          from_user_id: currentUser.id,
          to_user_id: likedUserId
        });

      if (superLikeError) throw superLikeError;

      // Record the like action
      const { error: actionError } = await supabase
        .from('user_actions')
        .insert({
          user_id: currentUser.id,
          target_user_id: likedUserId,
          action: 'super_like'
        });

      if (actionError) throw actionError;

      // Insert the like into matches table
      const { error } = await supabase
        .from('matches')
        .insert({
          user_id: currentUser.id,
          liked_user_id: likedUserId
        });

      if (error) throw error;

      // Check if the other user already liked us back
      const { data: mutualLike } = await supabase
        .from('matches')
        .select('*')
        .eq('user_id', likedUserId)
        .eq('liked_user_id', currentUser.id)
        .single();

      if (mutualLike) {
        setMatchedUser(currentProfile);
        setIsUberMatch(true);
        setShowMatchPopup(true);
        fetchMatches();
      } else {
        alert('⭐ Super Like sent! Your profile will stand out to them.');
      }

      // Move to next profile
      setCurrentProfileIndex(currentProfileIndex + 1);

      // Check if we should show an ad (free users only)
      if (!subscription?.is_premium) {
        const newAdCounter = adCounter + 1;
        setAdCounter(newAdCounter);
        
        // Show ad every 8 swipes
        if (newAdCounter % 8 === 0) {
          showRandomAd();
        }
      }

    } catch (error) {
      console.error('Error super liking:', error);
      alert('Failed to send Super Like. Please try again.');
    } finally {
      setLikesLoading(false);
    }
  };

  const handleLike = async (likedUserId) => {
  // Check if user can like
  if (!canLike()) {
    alert('You\'ve reached your daily limit of 10 likes. Upgrade to Premium for unlimited likes!');
    return;
  }

  setLikesLoading(true);
  
  try {
    // Check if user has already acted on this profile
    const { data: existingAction } = await supabase
      .from('user_actions')
      .select('*')
      .eq('user_id', currentUser.id)
      .eq('target_user_id', likedUserId)
      .single();

    let actionError;
    if (existingAction) {
      // Update existing record
      const { error } = await supabase
        .from('user_actions')
        .update({ action: 'like' })
        .eq('user_id', currentUser.id)
        .eq('target_user_id', likedUserId);
      actionError = error;
    } else {
      // Insert new record
      const { error } = await supabase
        .from('user_actions')
        .insert({
          user_id: currentUser.id,
          target_user_id: likedUserId,
          action: 'like'
        });
      actionError = error;
    }

    if (actionError) {
      console.error('Error recording like action:', actionError);
      return;
    }

    // Insert the like into matches table (for backward compatibility)
    const { error } = await supabase
      .from('matches')
      .insert({
        user_id: currentUser.id,
        liked_user_id: likedUserId
      });

    if (error) {
      console.error('Error liking user:', error);
      return;
    }

    // Update daily likes count for free users
    if (!subscription?.is_premium) {
      await incrementDailyLikes();
    }

    // Check if the other user already liked us back
    const { data: mutualLike } = await supabase
      .from('matches')
      .select('*')
      .eq('user_id', likedUserId)
      .eq('liked_user_id', currentUser.id)
      .single();

    if (mutualLike) {
      setMatchedUser(currentProfile);
      setIsUberMatch(false);
      setShowMatchPopup(true);
      // Refresh matches to show chat button
      fetchMatches();
    }

    // Move to next profile
    setCurrentProfileIndex(currentProfileIndex + 1);

    // Check if we should show an ad (free users only)
    if (!subscription?.is_premium) {
      const newAdCounter = adCounter + 1;
      setAdCounter(newAdCounter);
      
      // Show ad every 8 swipes
      if (newAdCounter % 8 === 0) {
        showRandomAd();
      }
    }

  } catch (error) {
    console.error('Error:', error);
  } finally {
    setLikesLoading(false);
  }
};
  
// Check if user should see mandatory ad
const checkMandatoryAd = async (userToCheck = null, pageLocation = 'discovery') => {
  const effectiveUser = userToCheck || currentUser;
  console.log('🔍 checkMandatoryAd called for page:', pageLocation);
  console.log('🔍 sessionMandatoryAdShown:', sessionMandatoryAdShown);
  console.log('🔍 effectiveUser:', effectiveUser);

  if (sessionMandatoryAdShown || !effectiveUser) {
    console.log('🔍 Exiting early - session shown or no user');
    return;
  }

  try {
    // Get ad settings
    const { data: settings } = await supabase
      .from('ad_settings')
      .select('*');

    const settingsObj = {};
    settings.forEach(setting => {
      settingsObj[setting.setting_name] = parseInt(setting.setting_value);
    });

    const dailyLimit = settingsObj.daily_mandatory_limit || 3;

    // Check today's mandatory ads count
    const today = new Date().toISOString().split('T')[0];
    const { data: todayAds } = await supabase
      .from('user_mandatory_ads')
      .select('*')
      .eq('user_id', effectiveUser.id)
      .gte('viewed_at', `${today}T00:00:00.000Z`)
      .lt('viewed_at', `${today}T23:59:59.999Z`);

    if (todayAds && todayAds.length >= dailyLimit) {
      return; // User has reached daily limit
    }

    // Get active mandatory ads
    const { data: mandatoryAds } = await supabase
      .from('mandatory_ads')
      .select('*')
      .eq('is_active', true);

    console.log('🔍 Found mandatory ads:', mandatoryAds); // ADD THIS LINE

    if (!mandatoryAds || mandatoryAds.length === 0) return;

    // Filter ads based on page location
    let filteredAds;
    if (pageLocation === 'discovery') {
      // Show only video ads on discovery
      filteredAds = mandatoryAds.filter(ad => ad.type === 'video');
    } else if (pageLocation === 'chat') {
      // Show only website ads on chat  
      filteredAds = mandatoryAds.filter(ad => ad.type === 'website');
    } else if (pageLocation === 'profile') {
      // Show both types on profile
      filteredAds = mandatoryAds;
    }
    console.log('🔍 Filtered ads for discovery:', filteredAds); // ADD THIS LINE
    if (filteredAds.length === 0) return;
    
    // Select random ad from filtered results
    const selectedAd = filteredAds[Math.floor(Math.random() * filteredAds.length)];

    setMandatoryAdData(selectedAd);
    setMandatoryAdType(selectedAd.type);
    setShowMandatoryAd(true);
    setSessionMandatoryAdShown(true);
    sessionStorage.setItem('mandatoryAdShown', 'true');

  } catch (error) {
    console.error('Error checking mandatory ad:', error);
  }
};

// Handle mandatory ad close
const handleMandatoryAdClose = async () => {
  if (mandatoryAdData && currentUser) {
    try {
      // Record the ad view
      await supabase
        .from('user_mandatory_ads')
        .insert({
          user_id: currentUser.id,
          ad_id: mandatoryAdData.id,
          page_location: 'discovery'
        });
    } catch (error) {
      console.error('Error recording mandatory ad view:', error);
    }
  }

  setShowMandatoryAd(false);
  setMandatoryAdData(null);
  setMandatoryAdType(null);
};
  const handlePass = async (passedUserId) => {
    try {
      // Record the pass action
      const { error } = await supabase
        .from('user_actions')
        .insert({
          user_id: currentUser.id,
          target_user_id: passedUserId,
          action: 'pass'
        });

      if (error) {
        console.error('Error recording pass action:', error);
        return;
      }

      // Move to next profile
      setCurrentProfileIndex(currentProfileIndex + 1);

      // Check if we should show an ad (free users only)
      if (!subscription?.is_premium) {
        const newAdCounter = adCounter + 1;
        setAdCounter(newAdCounter);
        
        // Show ad every 8 swipes
        if (newAdCounter % 8 === 0) {
          showRandomAd();
        }
      }

    } catch (error) {
      console.error('Error:', error);
    }
  };

  const isMatched = (userId) => {
    return matches.includes(userId);
  };

  const currentProfile = profiles[currentProfileIndex];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Finding amazing people for you...
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '40px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎉</div>
          <h2 style={{ 
            color: '#00E9FF', 
            marginBottom: '15px',
            fontSize: '24px'
          }}>
            You've seen everyone!
          </h2>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            Check back later for new profiles, or visit your matches to start chatting!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {/* Ad Banner */}
      {showAd && currentAdData && (
        <AdBanner 
          adData={currentAdData}
          onClose={() => {
            setShowAd(false);
            setCurrentAdData(null);
          }} 
        />
      )}

      <div style={{ 
        maxWidth: '400px', 
        width: '100%'
      }}>
        {/* Header with likes counter */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px'
        }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Discover
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '14px',
            margin: 0
          }}>
            {profiles.length - currentProfileIndex} people nearby (ages {currentUserProfile?.age_range_min || 18}-{currentUserProfile?.age_range_max || 50})
          </p>
          
          {/* Daily likes counter for free users */}
          {!subscription?.is_premium && (
            <div style={{
              marginTop: '10px',
              padding: '8px 16px',
              background: dailyLikes.likes_used >= 8 
                ? 'rgba(255, 107, 107, 0.2)' 
                : 'rgba(0, 233, 255, 0.2)',
              borderRadius: '15px',
              border: `1px solid ${dailyLikes.likes_used >= 8 ? 'rgba(255, 107, 107, 0.4)' : 'rgba(0, 233, 255, 0.4)'}`,
              display: 'inline-block'
            }}>
              <span style={{
                color: dailyLikes.likes_used >= 8 ? '#ff6b6b' : '#00E9FF',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                ❤️ {10 - dailyLikes.likes_used} likes left today
              </span>
            </div>
          )}
        </div>

        {/* Profile Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '25px',
          padding: '30px',
          backdropFilter: 'blur(10px)',
          border: didSuperLikeMe(currentProfile.id) 
            ? '3px solid #FFD700' 
            : '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
          position: 'relative',
          boxShadow: didSuperLikeMe(currentProfile.id)
            ? '0 20px 40px rgba(255, 215, 0, 0.3)'
            : '0 20px 40px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Super Like Badge */}
          {didSuperLikeMe(currentProfile.id) && (
            <div style={{
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(90deg, #FFD700, #FFA500)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '700',
              zIndex: 10,
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)'
            }}>
              ⭐ SUPER LIKED YOU
            </div>
          )}

          {/* Match Badge */}
          {isMatched(currentProfile.id) && (
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '15px',
              fontSize: '11px',
              fontWeight: '600',
              zIndex: 10
            }}>
              ✨ MATCHED
            </div>
          )}

          {/* Compatibility Score */}
          {!isMatched(currentProfile.id) && currentProfile.compatibilityScore > 0 && (
            <div style={{
              position: 'absolute',
              top: didSuperLikeMe(currentProfile.id) ? '25px' : '15px',
              left: '15px',
              background: currentProfile.compatibilityScore >= 70 
                ? 'linear-gradient(90deg, #51cf66, #40c057)' 
                : currentProfile.compatibilityScore >= 40
                ? 'linear-gradient(90deg, #ffd43b, #fab005)'
                : 'linear-gradient(90deg, #ff8787, #fa5252)',
              color: 'white',
              padding: '6px 10px',
              borderRadius: '15px',
              fontSize: '11px',
              fontWeight: '600',
              zIndex: 10
            }}>
              {currentProfile.compatibilityScore >= 70 ? '🔥' : 
               currentProfile.compatibilityScore >= 40 ? '⭐' : '💫'} 
              {Math.round(currentProfile.compatibilityScore)}%
            </div>
          )}

          {/* Profile Photo with Gallery */}
          <div style={{ marginBottom: '25px', position: 'relative' }}>
            {(() => {
              const photos = getCurrentProfilePhotos();
              const currentPhoto = photos[currentPhotoIndex];
              
              return (
                <div style={{ position: 'relative' }}>
                  {currentPhoto ? (
                    <img 
                      src={currentPhoto.photo_url} 
                      alt="Profile" 
                      style={{
                        width: '180px',
                        height: '180px',
                        borderRadius: '20px',
                        objectFit: 'cover',
                        border: didSuperLikeMe(currentProfile.id) 
                          ? '3px solid #FFD700' 
                          : '3px solid #00E9FF',
                        boxShadow: didSuperLikeMe(currentProfile.id)
                          ? '0 10px 30px rgba(255, 215, 0, 0.5)'
                          : '0 10px 30px rgba(0, 233, 255, 0.3)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '180px',
                      height: '180px',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      border: didSuperLikeMe(currentProfile.id) 
                        ? '3px solid #FFD700' 
                        : '3px solid #00E9FF',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '24px'
                    }}>
                      📷
                    </div>
                  )}

                  {/* Photo Navigation */}
                  {photos.length > 1 && (
                    <>
                      {/* Left Arrow */}
                      <button
                        onClick={prevPhoto}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          color: 'white',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.5)'}
                      >
                        ◀
                      </button>

                      {/* Right Arrow */}
                      <button
                        onClick={nextPhoto}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          color: 'white',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.5)'}
                      >
                        ▶
                      </button>

                      {/* Photo Indicators */}
                      <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '6px'
                      }}>
                        {photos.map((_, index) => (
                          <div
                            key={index}
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: index === currentPhotoIndex 
                                ? '#00E9FF' 
                                : 'rgba(255, 255, 255, 0.5)',
                              cursor: 'pointer',
                              transition: 'background 0.2s ease'
                            }}
                            onClick={() => setCurrentPhotoIndex(index)}
                          />
                        ))}
                      </div>

                      {/* Photo Counter */}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: isMatched(currentProfile.id) ? '150px' : '10px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        zIndex: 5
                      }}>
                        {currentPhotoIndex + 1}/{photos.length}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Profile Info */}
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            color: didSuperLikeMe(currentProfile.id) ? '#FFD700' : '#00E9FF',
            marginBottom: '15px'
          }}>
            {currentProfile.name || 'Anonymous User'}
            {currentProfile.age && (
              <span style={{ 
                fontSize: '24px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontWeight: '400'
              }}>
                , {currentProfile.age}
              </span>
            )}
          </h2>
          
          {currentProfile.location && (
            <div style={{ 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>📍</span>
              <span style={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '16px'
              }}>
                {currentProfile.location}
              </span>
            </div>
          )}
          
          {currentProfile.bio && (
            <div style={{ 
              marginBottom: '20px',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{ 
                margin: 0,
                lineHeight: '1.6', 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px'
              }}>
                {currentProfile.bio}
              </p>
            </div>
          )}

          {/* Common Interests */}
          {currentProfile.compatibilityScore > 0 && (
            <div style={{ 
              marginBottom: '20px',
              padding: '15px',
              background: 'rgba(0, 233, 255, 0.1)',
              borderRadius: '15px',
              border: '1px solid rgba(0, 233, 255, 0.3)'
            }}>
              <h4 style={{ 
                margin: '0 0 10px 0',
                color: '#00E9FF',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                🤝 What you have in common:
              </h4>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px' 
              }}>
                {getCommonInterests(currentProfile).map((interest, index) => (
                  <span key={index} style={{
                    padding: '4px 10px',
                    background: 'rgba(0, 233, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#00E9FF',
                    border: '1px solid rgba(0, 233, 255, 0.4)'
                  }}>
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          {isMatched(currentProfile.id) ? (
            <button 
              onClick={() => onChatOpen(currentProfile)}
              style={{
                width: '100%',
                height: '55px',
                border: 'none',
                background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                color: 'white',
                borderRadius: '15px',
                fontWeight: '700',
                fontSize: '18px',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif',
                transition: 'transform 0.2s ease',
                boxShadow: '0 8px 32px rgba(0, 233, 255, 0.3)'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              💬 Start Chatting
            </button>
          ) : (
            <div style={{ 
              display: 'flex', 
              gap: '15px',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {/* Pass Button */}
              <button 
                onClick={() => handlePass(currentProfile.id)}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                  color: 'white',
                  fontSize: '32px',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'transform 0.2s ease',
                  boxShadow: '0 8px 20px rgba(255, 107, 107, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                ❌
              </button>

              {/* Super Like Button (Premium only) */}
              {subscription?.is_premium && (
                <button 
                  onClick={() => handleSuperLike(currentProfile.id)}
                  disabled={likesLoading}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    color: 'white',
                    fontSize: '24px',
                    cursor: likesLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    transition: 'transform 0.2s ease',
                    boxShadow: '0 8px 20px rgba(255, 215, 0, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: likesLoading ? 0.7 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!likesLoading) e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseOut={(e) => {
                    if (!likesLoading) e.target.style.transform = 'scale(1)';
                  }}
                >
                  ⭐
                </button>
              )}

              {/* Regular Like Button */}
              <button 
                onClick={() => handleLike(currentProfile.id)}
                disabled={likesLoading || (!canLike() && !subscription?.is_premium)}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: 'none',
                  background: !canLike() && !subscription?.is_premium 
                    ? 'linear-gradient(135deg, #666, #555)' 
                    : 'linear-gradient(135deg, #51cf66, #40c057)',
                  color: 'white',
                  fontSize: '32px',
                  cursor: (!canLike() && !subscription?.is_premium) ? 'not-allowed' : 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'transform 0.2s ease',
                  boxShadow: !canLike() && !subscription?.is_premium 
                    ? '0 8px 20px rgba(102, 102, 102, 0.3)' 
                    : '0 8px 20px rgba(81, 207, 102, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: likesLoading ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (canLike() || subscription?.is_premium) {
                    e.target.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (canLike() || subscription?.is_premium) {
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                {likesLoading ? '⏳' : '❤️'}
              </button>
            </div>
          )}

          {/* Super Like explanation for free users */}
          {!subscription?.is_premium && (
            <div style={{
              marginTop: '15px',
              padding: '12px',
              background: 'rgba(255, 215, 0, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 215, 0, 0.3)'
            }}>
              <p style={{
                margin: 0,
                color: '#FFD700',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                ⭐ Super Likes available with Premium - Stand out from the crowd!
              </p>
            </div>
          )}

          {/* Upgrade prompt for free users who hit limit */}
          {!subscription?.is_premium && !canLike() && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: 'rgba(255, 107, 107, 0.1)',
              borderRadius: '15px',
              border: '1px solid rgba(255, 107, 107, 0.3)'
            }}>
              <p style={{
                margin: '0 0 10px 0',
                color: '#ff6b6b',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ⏰ Daily likes used up!
              </p>
              <p style={{
                margin: '0 0 15px 0',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '13px'
              }}>
                Upgrade to Premium for unlimited likes and Super Likes!
              </p>
              <button
                onClick={() => window.location.hash = '#subscription'}
                style={{
                  background: 'linear-gradient(90deg, #ff6b6b, #ee5a52)',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                💎 Upgrade Now
              </button>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '20px',
          color: 'rgba(255, 255, 255, 0.5)',
          fontSize: '14px'
        }}>
          {currentProfileIndex + 1} of {profiles.length}
        </div>

        {/* Mandatory Ad Modal */}
        {showMandatoryAd && (
          <MandatoryAd
            onClose={handleMandatoryAdClose}
            adData={mandatoryAdData}
            adType={mandatoryAdType}
          />
        )}
      </div>
      {/* Modern Match Popup */}
        {showMatchPopup && matchedUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1500,
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{
              background: isUberMatch 
                ? 'linear-gradient(135deg, #FFD700, #FFA500)' 
                : 'linear-gradient(135deg, #00E9FF, #8A2BE2)',
              borderRadius: '25px',
              padding: '40px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              position: 'relative',
              boxShadow: isUberMatch
                ? '0 30px 60px rgba(255, 215, 0, 0.5)'
                : '0 30px 60px rgba(0, 233, 255, 0.5)',
              animation: 'matchPopup 0.5s ease-out'
            }}>
              {/* Celebration Icons */}
              <div style={{
                fontSize: '60px',
                marginBottom: '20px',
                animation: 'bounce 1s infinite'
              }}>
                {isUberMatch ? '⭐🎉⭐' : '🎉💕🎉'}
              </div>

              {/* Match Title */}
              <h2 style={{
                color: 'white',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 0 15px 0',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
              }}>
                {isUberMatch ? "SUPER MATCH!" : "IT'S A MATCH!"}
              </h2>

              {/* Match Description */}
              <p style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '18px',
                margin: '0 0 25px 0',
                lineHeight: '1.4'
              }}>
                You and <strong>{matchedUser.name}</strong> liked each other!
              </p>

              {/* Profile Photos */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid white',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
                }}>
                  <img 
                    src={matchedUser.photo_url || '/default-avatar.png'} 
                    alt={matchedUser.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '24px'
                }}>
                  💕
                </div>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid white',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  👤
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => {
                    setShowMatchPopup(false);
                    onChatOpen(matchedUser);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid white',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = isUberMatch ? '#FFD700' : '#00E9FF';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.color = 'white';
                  }}
                >
                  💬 Start Chat
                </button>

                <button
                  onClick={() => setShowMatchPopup(false)}
                  style={{
                    background: 'transparent',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 24px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = 'white';
                    e.target.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                  }}
                >
                  Keep Swiping
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default Discovery;